import React, { useState } from 'react'
import { AdCreative } from '@marketing-milk/interfaces'
import { CardRibbon } from 'components/cards/action-card/components/card-ribbon'
import { CreativePreview, getIFrameSource } from 'components/ad-preview'
import { Drawer, Tooltip, Badge } from '@material-ui/core'
import { sentenceCase } from 'change-case'
import { CreativePill } from './CreativePill'
import { CreativeVerdictBadge, hasClientApproval } from './CreativeVerdictBadge'
import { CreativeMedia } from './CreativeMedia'
import { CreativeTools } from '../components/creative-tools/CreativeTools'
import { LeadCardIcon } from './LeadCardIcon'
import { useFetchCreativePreview } from '../../../../hooks/creatives/fetch-creative-preview'
import CommentIcon from '@material-ui/icons/Comment'
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    commentBadge: {
      position: 'absolute',
      top: theme.spacing(2),
      right: theme.spacing(6),
      zIndex: 10,
    },
  })
)

type StringKeys<T> = { [P in keyof T]: NonNullable<T[P]> extends String ? P : never }[keyof T]

type Props = {
  creative: AdCreative
  selected?: boolean
  /** Ribbon label for a selected card. Separate from `selected` because the label is
   * the campaign picker's ad-set letter, which means nothing on screens that pick ads
   * for some other reason — they mark the card without stamping a letter on it. */
  selectedText?: string
  highlighted?: boolean
  /** Dims the card and blocks the click outright. */
  disabled?: boolean
  /**
   * Why this ad is withheld from being picked, stamped on it. Separate from
   * `disabled` because a withheld ad that is already attached still has to be
   * clickable — otherwise the ads that arrive pre-attached have no way off the
   * campaign on the screen that reports them as the problem.
   */
  withheldReason?: string
  pillDisplayed?: keyof Pick<AdCreative, 'format' | 'status' | 'reviewStatus'>
  propertiesDisplayed?: StringKeys<AdCreative>[]
  tools?: boolean
  objectFit?: 'cover' | 'contain'
  onSelect: (adCreative: AdCreative) => void
}
export const CreativeCard = ({
  creative,
  propertiesDisplayed = ['name'],
  tools = true,
  objectFit,
  ...props
}: Props) => {
  const classes = useStyles()
  const buttonClasses =
    'font-semibold text-lg text-center p-3 w-full transition-all duration-200 bg-gray-300 hover:bg-blue-800'
  const [viewPreview, setViewPreview] = useState(false)

  const { adCreativePreview, retrievePreview, loadingPreview, refreshing } =
    useFetchCreativePreview(creative.id, undefined, { suppressErrorToast: true })
  // Comment count badge dropped during comments unification — bulk creative listing no
  // longer embeds comments. Reintroduce via a count aggregate if AEs miss it.
  const commentCount = 0

  const dimmed = props.disabled || !!props.withheldReason

  const className = `m-2 transition-all z-0 overflow-hidden duration-200 w-80 min-h-96 rounded-2xl relative flex flex-col shadow-xl ${
    dimmed ? 'opacity-50' : 'hover:shadow-2xl hover:border-blue-200'
  } ${props.disabled ? 'cursor-not-allowed' : 'cursor-pointer'} ${
    props.selected ?? Boolean(props.selectedText)
      ? 'border-2 border-purple-600 hover:border-purple-600'
      : props.highlighted
      ? 'border-2 border-amber-400 hover:border-amber-400'
      : 'border hover:border-purple-400'
  }`

  return (
    <section className={className} data-testid={creative.name}>
      <div className="flex items-center justify-end">
        {tools && <CreativeTools creative={creative} dotPattern="h" />}
      </div>

      <div onClick={() => !props.disabled && props.onSelect(creative)}>
        {/* One ribbon slot, so the withheld reason takes it when there is one — that
            is the news, and selection is still readable off the purple border. */}
        {props.withheldReason ? (
          <CardRibbon title={props.withheldReason} color="danger" />
        ) : props.disabled ? (
          <CardRibbon title="Published" color="info" />
        ) : (
          props.selectedText && <CardRibbon title={props.selectedText} />
        )}

        <CreativePill creative={creative} property="format" />

        <LeadCardIcon creative={creative} />

        {commentCount > 0 && (
          <Badge badgeContent={commentCount} color="secondary" className={classes.commentBadge}>
            <CommentIcon />
          </Badge>
        )}

        <CreativeMedia creative={creative} objectFit={objectFit} />

        {hasClientApproval(creative) && (
          <div className="px-2 pt-3 sm:px-6">
            <CreativeVerdictBadge />
          </div>
        )}

        <div className="divide-y flex-1">
          {propertiesDisplayed.map(
            property =>
              property && (
                <div className={'py-3 px-2 sm:py-3 sm:px-6 grid grid-cols-1'}>
                  <dt className={'text-sm font-medium'}>
                    <span className={!creative[property] ? 'text-red-800' : 'text-gray-500'}>
                      {sentenceCase(property)} {!creative[property] && '(incomplete)'}
                    </span>
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 sm:mt-0 sm:col-span-2 truncate">
                    {creative[property]}
                  </dd>
                </div>
              )
          )}
        </div>
      </div>

      <div className="rounded-b-2xl flex justify-center w-full divide-x">
        {creative.destinationURL && (
          <Tooltip title={creative.destinationURL}>
            <a
              href={creative.destinationURL}
              target="_blank"
              className={`${buttonClasses} border-r border-gray-800`}
            >
              Open Link
            </a>
          </Tooltip>
        )}
        <a
          href="#"
          className={buttonClasses}
          onClick={e => {
            e.stopPropagation()
            setViewPreview(true)
            retrievePreview()
          }}
        >
          Preview Ad
        </a>
      </div>

      <Drawer open={viewPreview} onClose={() => setViewPreview(false)}>
        <div className="flex flex-col p-4 h-screen overflow-hidden w-full space-y-4 ">
          <div className="flex justify-between align-middle items-center">
            <p className="text-center font-extrabold text-xl">Ad Preview</p>
            <i className="fa fa-times flex cursor-pointer" onClick={() => setViewPreview(false)} />
          </div>
          {/* The preview can be taller than the drawer; scroll it (rather than
              clip-centering) so the unavailable banner + authorizer link below the
              card stay reachable. Thin scrollbar matches the nav/present pattern. */}
          <style>{`
            .ad-preview-scroll::-webkit-scrollbar { width: 6px; }
            .ad-preview-scroll::-webkit-scrollbar-track { background: transparent; }
            .ad-preview-scroll::-webkit-scrollbar-thumb { background: #dadde1; border-radius: 3px; }
            .ad-preview-scroll::-webkit-scrollbar-thumb:hover { background: #c4cdd6; }
          `}</style>
          <div
            className="flex-1 overflow-y-auto flex flex-col items-center ad-preview-scroll"
            style={{ scrollbarWidth: 'thin', scrollbarColor: '#dadde1 transparent' }}
          >
            <CreativePreview
              creative={creative}
              iframeSource={getIFrameSource(adCreativePreview ?? '')}
              loading={loadingPreview}
              refreshing={refreshing}
              onRetry={() => retrievePreview()}
            />
          </div>
        </div>
      </Drawer>
    </section>
  )
}
